<template>
  <div class="shapes-wrap">
    <!-- Header -->
    <div class="header">
      <button class="back-btn" @click="goBack">← بازگشت</button>
      <h1 class="title">بازی اشکال 🔺</h1>
      <div class="diff-pills">
        <button 
          v-for="d in difficulties" 
          :key="d.key" 
          class="diff-pill" 
          :class="{ active: level === d.key }"
          @click="setLevel(d.key)"
          :disabled="started"
        >
          {{ d.label }}
        </button>
      </div>
    </div>

    <!-- Game Stats & Controls -->
    <div class="game-controls">
      <div class="stats">
        <div class="stat-box">
          <span class="stat-icon">⏱️</span>
          <span class="stat-value">{{ timeText }}</span>
        </div>
        <div class="stat-box">
          <span class="stat-icon">🎯</span>
          <span class="stat-value">{{ moves }} حرکت</span>
        </div>
      </div>
      <button class="start-btn" v-if="!started" @click="startGame">شروع بازی 🎮</button>
    </div>

    <!-- Game Area -->
    <div class="game-area">
      <!-- Target Board -->
      <div class="shapes-board" :style="boardStyle" ref="boardRef">
        <!-- Target slots (ghost shapes) -->
        <div 
          v-for="slot in targetSlots" 
          :key="'slot-'+slot.id" 
          class="shape-slot"
          :style="slotStyle(slot)"
        >
          <svg :width="shapeSize" :height="shapeSize" class="slot-shape">
            <template v-if="slot.type === 'square'">
              <rect x="4" y="4" :width="shapeSize-8" :height="shapeSize-8" rx="4" fill="rgba(139,92,246,0.1)" stroke="rgba(139,92,246,0.3)" stroke-width="2" stroke-dasharray="6,4"/>
            </template>
            <template v-else-if="slot.type === 'circle'">
              <circle :cx="shapeSize/2" :cy="shapeSize/2" :r="shapeSize/2-6" fill="rgba(139,92,246,0.1)" stroke="rgba(139,92,246,0.3)" stroke-width="2" stroke-dasharray="6,4"/>
            </template>
            <template v-else-if="slot.type === 'triangle'">
              <polygon :points="`${shapeSize/2},6 6,${shapeSize-6} ${shapeSize-6},${shapeSize-6}`" fill="rgba(139,92,246,0.1)" stroke="rgba(139,92,246,0.3)" stroke-width="2" stroke-dasharray="6,4"/>
            </template>
            
            <!-- Color Label Text -->
            <text 
              :x="shapeSize/2" 
              :y="shapeSize/2 + 5" 
              dominant-baseline="middle" 
              text-anchor="middle"
              fill="#9ca3af" 
              font-size="14" 
              font-weight="bold"
              style="pointer-events: none; user-select: none; font-family: 'Vazirmatn', sans-serif;"
            >
              {{ slot.colorName }}
            </text>
          </svg>
        </div>
        
        <!-- Placed shapes (on board) -->
        <div 
          v-for="shape in placedShapes" 
          :key="'placed-'+shape.id" 
          class="shape-piece placed"
          :style="placedShapeStyle(shape)"
        >
          <svg :width="shapeSize" :height="shapeSize">
            <template v-if="shape.type === 'square'">
              <rect x="2" y="2" :width="shapeSize-4" :height="shapeSize-4" rx="6" :fill="shape.color"/>
            </template>
            <template v-else-if="shape.type === 'circle'">
              <circle :cx="shapeSize/2" :cy="shapeSize/2" :r="shapeSize/2-4" :fill="shape.color"/>
            </template>
            <template v-else-if="shape.type === 'triangle'">
              <polygon :points="`${shapeSize/2},4 4,${shapeSize-4} ${shapeSize-4},${shapeSize-4}`" :fill="shape.color"/>
            </template>
          </svg>
        </div>
      </div>

      <!-- Shapes Tray -->
      <div class="shapes-tray">
        <div 
          v-for="shape in trayShapes" 
          :key="'tray-'+shape.id" 
          class="tray-shape"
          :class="{ dragging: draggingShape?.id === shape.id }"
          @pointerdown="onShapeDown($event, shape)"
        >
          <svg :width="shapeSize" :height="shapeSize">
            <template v-if="shape.type === 'square'">
              <rect x="2" y="2" :width="shapeSize-4" :height="shapeSize-4" rx="6" :fill="shape.color"/>
            </template>
            <template v-else-if="shape.type === 'circle'">
              <circle :cx="shapeSize/2" :cy="shapeSize/2" :r="shapeSize/2-4" :fill="shape.color"/>
            </template>
            <template v-else-if="shape.type === 'triangle'">
              <polygon :points="`${shapeSize/2},4 4,${shapeSize-4} ${shapeSize-4},${shapeSize-4}`" :fill="shape.color"/>
            </template>
          </svg>
        </div>
      </div>
    </div>

    <!-- Dragging shape (follows cursor) -->
    <div 
      v-if="draggingShape" 
      class="dragging-shape"
      :style="draggingStyle"
    >
      <svg :width="shapeSize" :height="shapeSize">
        <template v-if="draggingShape.type === 'square'">
          <rect x="2" y="2" :width="shapeSize-4" :height="shapeSize-4" rx="6" :fill="draggingShape.color"/>
        </template>
        <template v-else-if="draggingShape.type === 'circle'">
          <circle :cx="shapeSize/2" :cy="shapeSize/2" :r="shapeSize/2-4" :fill="draggingShape.color"/>
        </template>
        <template v-else-if="draggingShape.type === 'triangle'">
          <polygon :points="`${shapeSize/2},4 4,${shapeSize-4} ${shapeSize-4},${shapeSize-4}`" :fill="draggingShape.color"/>
        </template>
      </svg>
    </div>

    <!-- Success Popup -->
    <div v-if="showSuccess" class="success-overlay">
      <div class="success-popup">
        <div class="confetti">🎉</div>
        <h2>آفرین! همه اشکال رو درست گذاشتی! 🌟</h2>
        <p class="success-stats">
          زمان: {{ timeText }} • حرکت‌ها: {{ moves }} • سطح: {{ levelLabel }}
        </p>
        <div class="success-actions">
          <button class="action-btn replay" @click="replayGame">🔄 بازی دوباره</button>
          <button class="action-btn back" @click="goBack">🏠 بازگشت به بازی‌ها</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation } from '@vue/apollo-composable'
import { SUBMIT_SHAPES_MUTATION } from '../api/mutations'

const router = useRouter()
const route = useRoute()
const slug = route.params.slug

const difficulties = [
  { key: 'easy', label: 'آسان', count: 4 },
  { key: 'medium', label: 'متوسط', count: 6 },
  { key: 'hard', label: 'سخت', count: 9 }
]

const level = ref('easy')
const started = ref(false)
const moves = ref(0)
const seconds = ref(0)
const showSuccess = ref(false)
const shapes = reactive([])
const targetSlots = reactive([])
const draggingShape = ref(null)
const dragPosition = reactive({ x: 0, y: 0 })
const boardRef = ref(null)
let timerInterval = null

const { mutate: submitShapes } = useMutation(SUBMIT_SHAPES_MUTATION)

// Shape colors palette with Names
const colorPalette = [
  { hex: '#ef4444', name: 'قرمز' },
  { hex: '#f59e0b', name: 'نارنجی' },
  { hex: '#10b981', name: 'سبز' },
  { hex: '#3b82f6', name: 'آبی' },
  { hex: '#8b5cf6', name: 'بنفش' },
  { hex: '#ec4899', name: 'صورتی' },
  { hex: '#14b8a6', name: 'فیروزه‌ای' },
  { hex: '#f97316', name: 'پرتقالی' },
  { hex: '#6366f1', name: 'نیلی' }
]

// Computed
const shapeCount = computed(() => {
  const d = difficulties.find(x => x.key === level.value)
  return d ? d.count : 4
})

const boardSize = computed(() => {
  return Math.min(340, window.innerWidth - 40)
})

const shapeSize = computed(() => {
  const cols = Math.ceil(Math.sqrt(shapeCount.value))
  const gap = 8
  return Math.floor((boardSize.value - (cols + 1) * gap) / cols)
})

const boardStyle = computed(() => ({
  width: boardSize.value + 'px',
  height: boardSize.value + 'px'
}))

const timeText = computed(() => {
  const m = Math.floor(seconds.value / 60).toString().padStart(2, '0')
  const s = (seconds.value % 60).toString().padStart(2, '0')
  return `${m}:${s}`
})

const levelLabel = computed(() => {
  const d = difficulties.find(x => x.key === level.value)
  return d ? d.label : 'آسان'
})

const trayShapes = computed(() => shapes.filter(s => !s.isPlaced))
const placedShapes = computed(() => shapes.filter(s => s.isPlaced))

const draggingStyle = computed(() => ({
  width: shapeSize.value + 'px',
  height: shapeSize.value + 'px',
  left: (dragPosition.x - shapeSize.value/2) + 'px',
  top: (dragPosition.y - shapeSize.value/2) + 'px'
}))

// Methods
function goBack() {
  router.push(`/room/${slug}`)
}

function setLevel(l) {
  if (started.value) return
  level.value = l
  initGame()
}

function initGame() {
  shapes.splice(0, shapes.length)
  targetSlots.splice(0, targetSlots.length)
  
  const count = shapeCount.value
  const cols = Math.ceil(Math.sqrt(count))
  const size = shapeSize.value
  const gap = 8
  const boardW = boardSize.value
  
  const totalGridW = cols * size + (cols - 1) * gap
  const startX = (boardW - totalGridW) / 2
  const startY = (boardW - totalGridW) / 2
  
  const shapeTypes = ['square', 'circle', 'triangle']
  
  // Create target slots
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / cols)
    const col = i % cols
    const type = shapeTypes[i % shapeTypes.length]
    
    targetSlots.push({
      id: i,
      type,
      x: startX + col * (size + gap),
      y: startY + row * (size + gap),
      filled: false,
      colorName: '' // Will be populated in next loop
    })
  }
  
  // Create shapes (shuffled logic later)
  const shapesData = []
  for (let i = 0; i < count; i++) {
    const type = shapeTypes[i % shapeTypes.length]
    const colorObj = colorPalette[i % colorPalette.length]
    
    // Assign color name to the matching slot before shuffling
    if (targetSlots[i]) {
      targetSlots[i].colorName = colorObj.name
    }

    shapesData.push({
      id: i,
      type,
      color: colorObj.hex,
      targetSlotId: i, // Which slot this shape should go to
      isPlaced: false
    })
  }
  
  // Shuffle shapes array so they appear random in tray
  for (let i = shapesData.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shapesData[i], shapesData[j]] = [shapesData[j], shapesData[i]]
  }
  
  shapes.push(...shapesData)
}

function startGame() {
  started.value = true
  moves.value = 0
  seconds.value = 0
  showSuccess.value = false
  initGame()
  startTimer()
}

function startTimer() {
  stopTimer()
  timerInterval = setInterval(() => {
    seconds.value++
  }, 1000)
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

function slotStyle(slot) {
  return {
    width: shapeSize.value + 'px',
    height: shapeSize.value + 'px',
    left: slot.x + 'px',
    top: slot.y + 'px'
  }
}

function placedShapeStyle(shape) {
  const slot = targetSlots.find(s => s.id === shape.targetSlotId)
  if (!slot) return {}
  return {
    width: shapeSize.value + 'px',
    height: shapeSize.value + 'px',
    left: slot.x + 'px',
    top: slot.y + 'px'
  }
}

// Drag handling
function onShapeDown(e, shape) {
  if (!started.value) return
  
  e.preventDefault()
  draggingShape.value = shape
  
  const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0
  const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0
  
  dragPosition.x = clientX
  dragPosition.y = clientY
  
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
}

function onPointerMove(e) {
  if (!draggingShape.value) return
  
  const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0
  const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0
  
  dragPosition.x = clientX
  dragPosition.y = clientY
}

function onPointerUp(e) {
  if (!draggingShape.value) return
  
  const shape = draggingShape.value
  const size = shapeSize.value
  
  const board = boardRef.value
  if (!board) {
    draggingShape.value = null
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    return
  }
  
  const boardRect = board.getBoundingClientRect()
  const clientX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? dragPosition.x
  const clientY = e.clientY ?? e.changedTouches?.[0]?.clientY ?? dragPosition.y
  
  // Check if dropped on the board
  if (
    clientX >= boardRect.left && 
    clientX <= boardRect.right && 
    clientY >= boardRect.top && 
    clientY <= boardRect.bottom
  ) {
    const dropX = clientX - boardRect.left
    const dropY = clientY - boardRect.top
    
    // Find the target slot for this shape
    const targetSlot = targetSlots.find(s => s.id === shape.targetSlotId)
    
    if (targetSlot && !targetSlot.filled) {
      // Check if drop is close to the correct slot
      const slotCenterX = targetSlot.x + size / 2
      const slotCenterY = targetSlot.y + size / 2
      const distance = Math.hypot(dropX - slotCenterX, dropY - slotCenterY)
      
      if (distance < size * 0.6) {
        // Place the shape!
        shape.isPlaced = true
        targetSlot.filled = true
        playSnapSound()
      }
    }
  }
  
  moves.value++
  draggingShape.value = null
  
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  
  checkComplete()
}

function playSnapSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 660
    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.15)
  } catch (e) { /* silent */ }
}

function checkComplete() {
  const allPlaced = shapes.every(s => s.isPlaced)
  if (allPlaced) {
    stopTimer()
    showSuccess.value = true
    submitSession()
  }
}

// --------------------------------------------------------------------------------
//  IMAGE GENERATION LOGIC FOR BACKEND (Reference & Composition)
// --------------------------------------------------------------------------------
async function submitSession() {
  try {
    // 1. Generate Reference Image (Empty slots)
    const refBase64 = await generateBoardImage(false)
    // 2. Generate Composition Image (Slots + Placed Shapes)
    const compBase64 = await generateBoardImage(true)

    await submitShapes({
      data: {
        roomSlug: slug,
        referenceBase64: refBase64,
        compositionBase64: compBase64,
        durationSeconds: seconds.value,
        moves: moves.value,
        level: level.value,
        shapeCount: shapes.length,
        declaredDone: true,
        success: true
      }
    })
  } catch (e) {
    console.error('Failed to submit shapes session', e)
  }
}

// Helper to draw board state to a canvas and return base64
function generateBoardImage(includePieces) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const size = boardSize.value
    // Double resolution for better quality
    const scale = 2 
    canvas.width = size * scale
    canvas.height = size * scale
    const ctx = canvas.getContext('2d')
    ctx.scale(scale, scale)

    // Background
    const gradient = ctx.createLinearGradient(0, 0, size, size)
    gradient.addColorStop(0, '#ffffff')
    gradient.addColorStop(1, '#f0fdf4')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, size, size)

    // Draw Border (visual match)
    ctx.strokeStyle = '#8b5cf6'
    ctx.lineWidth = 6
    ctx.strokeRect(0, 0, size, size)

    // Helper to draw shape path
    const drawShapePath = (type, x, y, s) => {
      ctx.beginPath()
      if (type === 'square') {
        // x, y passed are top-left of slot
        const pad = 2
        // Rounded rect
        const r = 6
        ctx.roundRect(x + pad, y + pad, s - pad*2, s - pad*2, r)
      } else if (type === 'circle') {
        const cx = x + s / 2
        const cy = y + s / 2
        const r = s / 2 - 4
        ctx.arc(cx, cy, r, 0, Math.PI * 2)
      } else if (type === 'triangle') {
        const pad = 4
        ctx.moveTo(x + s / 2, y + pad)
        ctx.lineTo(x + s - pad, y + s - pad)
        ctx.lineTo(x + pad, y + s - pad)
        ctx.closePath()
      }
    }

    const sSize = shapeSize.value

    // Draw Slots (Ghost outlines)
    targetSlots.forEach(slot => {
      drawShapePath(slot.type, slot.x, slot.y, sSize)
      ctx.fillStyle = 'rgba(139,92,246,0.1)'
      ctx.fill()
      ctx.strokeStyle = 'rgba(139,92,246,0.3)'
      ctx.lineWidth = 2
      if (!includePieces || !slot.filled) {
        // Dash if empty
        ctx.setLineDash([6, 4])
      } else {
         ctx.setLineDash([])
      }
      ctx.stroke()
      ctx.setLineDash([]) // reset
    })

    // Draw Pieces if requested
    if (includePieces) {
      shapes.forEach(shape => {
        if (shape.isPlaced) {
          // Find slot position
          const slot = targetSlots.find(s => s.id === shape.targetSlotId)
          if (slot) {
            drawShapePath(shape.type, slot.x, slot.y, sSize)
            ctx.fillStyle = shape.color
            ctx.fill()
            ctx.strokeStyle = 'rgba(0,0,0,0.1)'
            ctx.lineWidth = 1
            ctx.stroke()
          }
        }
      })
    }

    resolve(canvas.toDataURL('image/png'))
  })
}

function replayGame() {
  showSuccess.value = false
  startGame()
}

onMounted(() => {
  initGame()
})

onUnmounted(() => {
  stopTimer()
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
})
</script>

<script>
export default { name: 'ShapesGame' }
</script>

<style scoped>
.shapes-wrap {
  min-height: 100vh;
  padding: 16px;
  background: linear-gradient(135deg, #dbeafe 0%, #fce7f3 50%, #fef3c7 100%);
  font-family: 'Vazirmatn', sans-serif;
}

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.back-btn {
  background: linear-gradient(135deg, #fff 0%, #f0f9ff 100%);
  border: 3px solid #38bdf8;
  border-radius: 999px;
  padding: 10px 20px;
  font-weight: 700;
  font-size: 14px;
  color: #0369a1;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(56, 189, 248, 0.3);
  transition: all 0.3s ease;
}
.back-btn:hover {
  transform: scale(1.05);
}

.title {
  font-size: 28px;
  background: linear-gradient(135deg, #ef4444 0%, #f59e0b 50%, #10b981 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  flex: 1;
  text-align: center;
}

.diff-pills {
  display: flex;
  gap: 8px;
}

.diff-pill {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #b45309;
  border: none;
  border-radius: 999px;
  padding: 10px 18px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.diff-pill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.diff-pill.active {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.4);
}

.game-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.stats {
  display: flex;
  gap: 12px;
}

.stat-box {
  background: linear-gradient(135deg, #fff 0%, #fef3c7 100%);
  border: 2px solid #fbbf24;
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-icon {
  font-size: 20px;
}

.stat-value {
  font-weight: 700;
  color: #92400e;
  font-size: 16px;
}

.start-btn {
  background: linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 14px 28px;
  font-size: 18px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
}

.game-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  max-width: 500px;
  margin: 0 auto;
}

.shapes-board {
  position: relative;
  background: linear-gradient(135deg, #fff 0%, #f0fdf4 100%);
  border: 6px solid #8b5cf6;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
}

.shape-slot {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slot-shape {
  opacity: 0.8;
}

.shape-piece.placed {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
}

/* Shapes Tray */
.shapes-tray {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  background: linear-gradient(135deg, #fff 0%, #fef3c7 100%);
  border: 4px solid #fbbf24;
  border-radius: 16px;
  padding: 20px;
  min-height: 80px;
  width: 100%;
  max-width: 360px;
}

.tray-shape {
  cursor: grab;
  touch-action: none;
  filter: drop-shadow(0 6px 12px rgba(0,0,0,0.25));
  transition: all 0.2s ease;
}
.tray-shape:hover {
  transform: scale(1.1);
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
}
.tray-shape.dragging {
  opacity: 0.3;
}

/* Dragging shape */
.dragging-shape {
  position: fixed;
  pointer-events: none;
  z-index: 9999;
  filter: drop-shadow(0 16px 32px rgba(0,0,0,0.4));
}

/* Success Popup */
.success-overlay {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(99, 102, 241, 0.9) 50%, rgba(236, 72, 153, 0.9) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
}

.success-popup {
  background: #fff;
  border-radius: 32px;
  padding: 40px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 24px 60px rgba(0,0,0,0.3);
}

.confetti {
  font-size: 80px;
  animation: confetti-bounce 1s ease-in-out infinite;
}
@keyframes confetti-bounce {
  0%, 100% { transform: translateY(0) rotate(-5deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
}

.success-popup h2 {
  font-size: 22px;
  background: linear-gradient(135deg, #7c3aed 0%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 16px 0 8px;
}

.success-stats {
  color: #6b7280;
  font-size: 16px;
  margin-bottom: 24px;
}

.success-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-btn {
  padding: 14px 24px;
  border: none;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.action-btn.replay {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  color: #fff;
}
.action-btn.back {
  background: linear-gradient(135deg, #f472b6 0%, #ec4899 100%);
  color: #fff;
}

@media (max-width: 600px) {
  .shapes-tray { box-sizing: border-box; }
  .header {
    flex-direction: column;
    text-align: center;
  }
  .title {
    order: -1;
    width: 100%;
  }
  .diff-pills {
    justify-content: center;
  }
}
</style>